/**
 * A CSV reader for the browser, so an upload screen can show what it is about to send.
 *
 * Deliberately not a dependency: this is thirty lines of grammar, and the app already ships a
 * 3MB bundle. It mirrors the backend's own parser (utils/csv/parseCsv.js) so the preview an
 * operator reads before pressing Import matches what the server will make of the same file —
 * the same BOM handling, the same delimiter sniff, the same quote rules. The SERVER remains the
 * authority; this is only ever a preview.
 */

/** Excel writes semicolon-delimited CSV wherever the comma is the decimal separator. */
function sniffDelimiter(text: string): string {
  const firstLine = text.split(/\r?\n/, 1)[0] || '';
  let inQuotes = false;
  let commas = 0;
  let semis = 0;
  for (const ch of firstLine) {
    if (ch === '"') inQuotes = !inQuotes;
    else if (!inQuotes && ch === ',') commas++;
    else if (!inQuotes && ch === ';') semis++;
  }
  return semis > commas ? ';' : ',';
}

/**
 * Rows of trimmed cells, header included, blank lines dropped.
 *
 * The leading BOM matters more than it looks: left in place it becomes part of the FIRST header
 * name, so `name` stops matching and every row reports a missing camera name.
 */
export function parseCsv(text: string): string[][] {
  const clean = String(text || '').replace(/^﻿/, '');
  const delimiter = sniffDelimiter(clean);

  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;

  for (let i = 0; i < clean.length; i++) {
    const ch = clean[i];
    if (inQuotes) {
      if (ch === '"') {
        if (clean[i + 1] === '"') { field += '"'; i++; } else inQuotes = false;
      } else field += ch;
      continue;
    }
    if (ch === '"') { inQuotes = true; continue; }
    if (ch === delimiter) { row.push(field.trim()); field = ''; continue; }
    if (ch === '\r') continue;
    if (ch === '\n') { row.push(field.trim()); rows.push(row); row = []; field = ''; continue; }
    field += ch;
  }
  if (field.length || row.length) { row.push(field.trim()); rows.push(row); }

  return rows.filter((r) => r.some((c) => c !== ''));
}

/** Header names, folded the way the backend folds them: "IP Address" and "ip-address" agree. */
export function normaliseHeader(header: string): string {
  return header.toLowerCase().replace(/[\s-]+/g, '_');
}

export default parseCsv;
